import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    useTheme,
    alpha,
    Paper,
    Chip,
    Divider,
    Stack,
    Tabs,
    Tab,
    Fade,
    useMediaQuery,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Code as CodeIcon,
    DesignServices as DesignIcon,
    PhoneAndroid as MobileIcon,
    Public as WebIcon,
    Storage as CloudIcon,
    AllInclusive as AllIcon,
    ArrowForward as ArrowForwardIcon,
    OpenInNew as OpenInNewIcon,
    Visibility as PreviewIcon,
    Close as CloseIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CaseStudiesPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isMobile = false;
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [selectedTab, setSelectedTab] = useState('all');
    const [previewProject, setPreviewProject] = useState(null);
    const [isHovering, setIsHovering] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const navigate = useNavigate();

    const categories = [
        { id: 'all', label: 'All', icon: <AllIcon /> },
        { id: 'web', label: 'Web', icon: <WebIcon /> },
        { id: 'mobile', label: 'Mobile', icon: <MobileIcon /> },
        // { id: 'design', label: 'Design', icon: <DesignIcon /> },
        // { id: 'cloud', label: 'Cloud', icon: <CloudIcon /> }
    ];

    const projects = [
        {
            id: 1,
            title: "Petal Box Media Buying System",
            category: 'web',
            url: 'https://petalbox.com/',
            iframeUrl: 'https://petalbox.com/',
            description: "Media buying system for gifting agency",
            details: "Developed a custom web-based system that achieved 50x faster performance with intuitive workflows and advanced analytics, simplifying users' daily operations.",
            technologies: ['React', 'Node.js', 'MongoDB', 'AWS'],
            results: [
                "50x faster performance",
                "Intuitive user workflows",
                "Advanced analytics dashboard"
            ]
        },
        {
            id: 2,
            title: "Flower Emporium eCommerce App",
            category: "web",
            url: "https://floweremporiumkuwait.com/",
            iframeUrl: "https://floweremporiumkuwait.com",
            description: "Elegant and user-friendly eCommerce platform for flower delivery services.",
            details: "A full-featured eCommerce web application built for a luxury flower delivery brand. The platform enables users to browse a curated selection of floral products, customize arrangements, and schedule deliveries. It includes admin tools for inventory, order tracking, and promotional management.",
            technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'Cloudflare', 'AWS S3'],
            results: [
                "Increased online sales and customer retention",
                "Seamless order placement and payment processing",
                "Mobile-optimized design for better user engagement",
                "Admin dashboard for real-time order and inventory management"
            ]
        },
        {
            id: 3,
            title: "Buy Flower Box eCommerce Platform",
            category: "mobile",
            url: "https://www.buyflowerbox.com/",
            iframeUrl: "https://www.buyflowerbox.com",
            description: "Scalable eCommerce platform for curated flower gift boxes.",
            details: "A robust and scalable online platform designed for selling premium flower boxes and gifts. The application is built using a microservices architecture powered by Spring Boot, ensuring modularity, reliability, and performance. It features a responsive user interface, dynamic product management, secure checkout, and integration with third-party delivery and payment services.",
            technologies: [
                "Spring Boot",
                "Spring Cloud",
                "Spring Security",
                "MySQL",
                "Docker",
                "Kubernetes",
                "React",
                "RabbitMQ",
                "Stripe API"
            ],
            results: [
                "Highly scalable architecture supporting peak seasonal traffic",
                "Microservices-based deployment with independent scaling and updates",
                "Secure user authentication and role-based access",
                "Efficient order processing and real-time delivery status updates",
                "Admin portal for managing products, promotions, and analytics"
            ]
        },
        {
            id: 4,
            title: "Karla Electronics Corporate Website",
            category: "web",
            url: "http://karlaelectronics.in/",
            iframeUrl: "http://karlaelectronics.in/",
            description: "Modern B2B website for an industrial electronics and automation company.",
            details: "A responsive corporate website designed to highlight Karla Electronics' range of industrial products and automation solutions. Built with a decoupled architecture using React for the frontend and Flask for backend APIs, the site delivers fast performance, structured product data, and easy navigation. Includes product catalogs, inquiry forms, and dynamic content sections managed via admin APIs.",
            technologies: [
                "React",
                "Flask",
                "PostgreSQL",
                "REST API",
                "Bootstrap",
                "Nginx",
                "Docker"
            ],
            results: [
                "Improved user experience and mobile accessibility",
                "Faster content delivery using optimized REST APIs",
                "Efficient product data management with backend CMS APIs",
                "SEO-friendly architecture boosting digital visibility",
                "Lead generation through real-time inquiry handling"
            ]
        },
        {
            id: 5,
            title: "EcoscapeME Corporate Website",
            category: "mobile",
            url: "https://www.ecoscapeme.com/",
            iframeUrl: "https://www.ecoscapeme.com/",
            description: "Elegant corporate website for a landscape design and construction firm in the Middle East.",
            details: "A visually rich and responsive website developed for EcoscapeME, a premier landscaping company offering design, construction, and maintenance services. The site features a dynamic portfolio gallery, service descriptions, client testimonials, and a contact system. Built with React and Flask, it delivers smooth navigation, content-driven storytelling, and fast backend interactions. Admin APIs allow content updates for projects, team, and service details.",
            technologies: [
                "React",
                "Flask",
                "PostgreSQL",
                "REST API",
                "Sass",
                "Swiper.js",
                "Docker",
                "Nginx"
            ],
            results: [
                "Modern, responsive interface improving brand perception",
                "Interactive portfolio showcasing past landscape projects",
                "Streamlined client inquiries through dynamic forms",
                "Optimized loading and SEO for better visibility",
                "Modular backend allowing content updates without code changes"
            ]
        },

        {
            id: 6,
            title: "Shreeshtech Digital Billing ERP System",
            category: 'web',
            url: 'https://shreeshtech.com/services',
            iframeUrl: 'https://shreeshtech.com/services',
            description: "Comprehensive ERP solution for digital billing and inventory management",
            details: "Developed a full-featured ERP system with digital billing, inventory management, multi-branch support, and real-time analytics. The system features role-based access control, offline capabilities with cloud sync, and automated backup systems.",
            technologies: [
                'Flutter',
                'React',
                'Node.js',
                'Microservices',
                'REST API',
                'Firebase',
                'MongoDB',
                'AWS S3',
                'Multi-platform'
            ],
            results: [
                "90% reduction in billing errors",
                "Real-time inventory tracking across branches",
                "Automated backup and data recovery",
                "Multi-platform support (Web, Mobile, Desktop)",
                "30% faster checkout process"
            ]
        }

    ];

    const filteredProjects = selectedTab === 'all'
        ? projects
        : projects.filter(project => project.category === selectedTab);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        setPreviewProject(null);
        if (mobileFiltersOpen) setMobileFiltersOpen(false);
    };

    const handleProjectClick = (project) => {
        setPreviewProject(project);
    };

    const handleClosePreview = () => {
        setPreviewProject(null);
    };

    useEffect(() => {
        if (previewProject && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [previewProject, isMobile]);

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            {/* Hero Section - Optimized for Mobile */}
            <Box sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`,
                color: 'common.white',
                py: isMobile ? 6 : 10,
                textAlign: 'center',
                px: isMobile ? 2 : 0
            }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" component="h1" sx={{
                        fontWeight: 800,
                        fontSize: isMobile ? '2rem' : '3.5rem',
                        lineHeight: 1.2,
                        mb: 2
                    }}>
                        Projects We Successfully Developed
                    </Typography>

                    <Typography variant={isMobile ? "body1" : "h5"} sx={{
                        maxWidth: 800,
                        mx: 'auto',
                        opacity: 0.9,
                        fontWeight: 400,
                        mb: 4
                    }}>
                        One look is worth a thousand words.
                    </Typography>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => navigate('/get-in-touch')}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        Get in Touch
                    </Button>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: isMobile ? 4 : 8, px: isMobile ? 2 : 3 }}>
                {/* Category Tabs - Mobile Optimized */}
                <Box sx={{
                    mb: 4,
                    position: 'sticky',
                    top: isMobile ? 56 : 0,
                    zIndex: 10,
                    bgcolor: 'background.default',
                    pt: isMobile ? 1 : 2
                }}>
                    {isMobile ? (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {categories.find(c => c.id === selectedTab)?.label} Projects
                                </Typography>
                                <IconButton
                                    onClick={() => setMobileFiltersOpen(true)}
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                            </Box>

                            <Dialog
                                open={mobileFiltersOpen}
                                onClose={() => setMobileFiltersOpen(false)}
                                fullScreen={isMobile}
                            >
                                <DialogContent sx={{ py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                                        Filter Projects
                                    </Typography>
                                    <Stack spacing={2}>
                                        {categories.map((category) => (
                                            <Button
                                                key={category.id}
                                                fullWidth
                                                startIcon={category.icon}
                                                onClick={() => handleTabChange(null, category.id)}
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    py: 2,
                                                    px: 3,
                                                    borderRadius: 1,
                                                    bgcolor: selectedTab === category.id ?
                                                        alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                                    color: selectedTab === category.id ?
                                                        theme.palette.primary.main : 'text.primary',
                                                    fontWeight: 600,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                {category.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setMobileFiltersOpen(false)}
                                    >
                                        Apply Filters
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    ) : (
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    minHeight: 48,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                    px: 2,
                                    mx: 0.5,
                                    borderRadius: 1,
                                    '&.Mui-selected': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main
                                    }
                                }
                            }}
                        >
                            {categories.map((category) => (
                                <Tab
                                    key={category.id}
                                    icon={category.icon}
                                    iconPosition="start"
                                    label={category.label}
                                    value={category.id}
                                    sx={{
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                                        }
                                    }}
                                />
                            ))}
                        </Tabs>
                    )}
                </Box>

                {/* Projects Grid */}
                <Box sx={{ mt: 6 }}>
                    {filteredProjects.map((project) => (
                        <Paper key={project.id} elevation={6} sx={{ borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                            <Grid container>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{
                                        height: { xs: 300, md: 500 },
                                        position: 'relative',
                                        bgcolor: 'grey.900'
                                    }}>
                                        <Box
                                            component="iframe"
                                            src={project.iframeUrl}
                                            title={project.title}
                                            loading="lazy"
                                            sx={{
                                                width: '60rem',
                                                height: '100%',
                                                border: 'none'
                                            }}
                                        />
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: 16,
                                            right: 16,
                                            zIndex: 2
                                        }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                href={project.url}
                                                // onClick={()=>navigate(`/${project.url}`)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                endIcon={<OpenInNewIcon />}
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 1,
                                                    fontWeight: 600,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                Visit Live Site
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                                        <Stack spacing={3} height="100%">
                                            <div>
                                                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    {project.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    {project.description}
                                                </Typography>
                                            </div>

                                            <Divider />

                                            <Typography variant="body1">
                                                {project.details}
                                            </Typography>

                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Key Results:
                                                </Typography>
                                                <ul style={{
                                                    paddingLeft: 20,
                                                    marginTop: 0,
                                                    listStyleType: 'none'
                                                }}>
                                                    {project.results.map((result, index) => (
                                                        <li key={index} style={{ marginBottom: 8 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                <Box sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    bgcolor: theme.palette.primary.main,
                                                                    borderRadius: '50%',
                                                                    mt: 1,
                                                                    mr: 1.5
                                                                }} />
                                                                <Typography variant="body1">{result}</Typography>
                                                            </Box>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Box>

                                            <Box sx={{ mt: 'auto' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Technologies Used:
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {project.technologies.map((tech, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={tech}
                                                            icon={<CodeIcon fontSize="small" />}
                                                            sx={{
                                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                color: theme.palette.primary.dark,
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Box>

                {/* Project Detail Preview - Mobile Optimized */}

            </Container>

            {/* CTA Section - Mobile Optimized */}
            <Box sx={{
                bgcolor: 'grey.50',
                py: isMobile ? 6 : 10,
                textAlign: 'center',
                backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0) 50%)',
                px: isMobile ? 2 : 0
            }}>
                <Container maxWidth="md">
                    <Typography variant={isMobile ? "h5" : "h4"} component="h2" sx={{ fontWeight: 700, mb: 3 }}>
                        Ready to Start Your Project?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{
                        maxWidth: 600,
                        mx: 'auto',
                        mb: 4,
                        fontSize: isMobile ? '0.875rem' : '1rem'
                    }}>
                        Let's discuss how we can help you achieve your business goals with our expertise.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/get-in-touch')}
                        size={isMobile ? "medium" : "large"}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            px: isMobile ? 4 : 6,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        Get in Touch
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default CaseStudiesPage;